import { useState } from 'react';
import { NJButton, NJText } from '@engie-group/fluid-design-system-react';
import { UMFormStyles, UMInput, UMR } from '../../urbanmap/_components';
import type { PlanGeoData } from '../proposition-c/types';
import { buildingsByIds } from './engine';
import { MapPane, type MapOverlay } from './MapPane';
import type { Project } from './types';
import { fmt } from './ui';

interface Props {
  project: Project; activePlanId: string; geo?: PlanGeoData; canWrite: boolean;
  onCreate: (name: string, buildingIds: string[]) => string;
  onUpdate: (id: string, changes: { name?: string; buildingIds?: string[] }) => void;
  onDelete: (id: string) => void;
}

export function SelectionsPanel(p: Props) {
  const [selectedId, setSelectedId] = useState(p.project.selections[0]?.id ?? '');
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [draftIds, setDraftIds] = useState<string[] | null>(null);
  const [area, setArea] = useState(false);
  const [message, setMessage] = useState('');
  const selected = p.project.selections.find((s) => s.id === selectedId);
  const plan = p.project.plans.find((pl) => pl.id === p.activePlanId) ?? p.project.plans[0];
  const ids = draftIds ?? selected?.buildingIds ?? [];
  const buildings = buildingsByIds(ids);
  const users = p.project.plans.filter((pl) => pl.selectionIds?.includes(selectedId));
  const dirty = draftIds !== null || creating;
  const overlay: MapOverlay = {
    selectedBuildings: plan?.buildingIds ?? [], selectionBuildings: ids,
    areaMode: area && p.canWrite, onAreaError: setMessage,
    onAreaSelect: ({ buildings: added }) => {
      setDraftIds([...new Set([...ids, ...added])]); setArea(false);
      setMessage(`${added.length} bâtiments dans la zone, ajoutés au brouillon de sélection.`);
    },
    onBuildingClick: p.canWrite ? (id) => {
      if (!dirty) setName(selected?.name ?? '');
      setDraftIds(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
    } : undefined,
    legend: [`Plan actif : ${plan?.name ?? 'Aucun'}. Un seul plan affiché.`,
      'Vert : plan actif (prioritaire) · violet : sélection hors plan.',
      'Les sélections copiées dans un plan ne sont jamais modifiées automatiquement.'],
  };
  return <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
    <section style={{ flex: '0 0 420px', overflowY: 'auto', padding: 16, borderRight: `1px solid ${UMR.border}` }}>
      <UMFormStyles />
      <NJText as="h2" scale="lg">Outils UM · Sélections</NJText>
      <NJText scale="xs">Exploration locale de la copie du périmètre, sans communication avec UrbanMap.</NJText>
      <NJText scale="sm">Plan actif : {plan?.name ?? 'Aucun'}</NJText>
      <NJText as="h3" scale="md">Toutes les sélections ({p.project.selections.length})</NJText>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {p.project.selections.map((s) => <NJButton key={s.id} label={`${s.name} · ${s.buildingIds.length} bâtiments`}
          variant={!creating && selectedId === s.id ? 'primary' : 'secondary'} scale="sm" disabled={dirty}
          onClick={() => { setSelectedId(s.id); setName(s.name); }} />)}
      </div>
      {!p.project.selections.length && <NJText>Aucune sélection. Créez-en une sur la carte.</NJText>}
      {p.canWrite && <NJButton label="Créer une sélection" variant="secondary" scale="sm" disabled={dirty}
        onClick={() => { setCreating(true); setName(''); setDraftIds([]); }} />}
      {(selected || creating) && <>
        <NJText as="h3" scale="md">{creating ? 'Nouvelle sélection' : selected?.name}</NJText>
        <NJText scale="sm">{ids.length} bâtiments · {fmt(buildings.reduce((sum, b) => sum + b.heat, 0))} MWh/an</NJText>
        <NJText scale="xs">Usages : {[...new Set(buildings.map((b) => b.usage))].join(', ') || '—'}</NJText>
        <NJText scale="xs">Utilisée par : {users.map((pl) => pl.name).join(' · ') || 'Aucun plan'}</NJText>
        {p.canWrite && <>
          <UMInput value={dirty ? name : selected?.name ?? ''} ariaLabel="Nom de la sélection" onChange={(v) => {
            setName(v); if (draftIds === null) setDraftIds([...ids]);
          }} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            <NJButton label={area ? 'Annuler le lasso' : 'Sélectionner par lasso'} scale="sm" variant="secondary"
              onClick={() => { if (!dirty) setName(selected?.name ?? ''); setArea(!area); }} />
            <NJButton label="Confirmer la sélection" scale="sm" disabled={!dirty || !name.trim() || !ids.length} onClick={() => {
              if (creating) setSelectedId(p.onCreate(name.trim(), ids));
              else if (selected) p.onUpdate(selected.id, { name: name.trim(), buildingIds: ids });
              setCreating(false); setDraftIds(null); setArea(false); setMessage('Sélection confirmée.');
            }} />
            <NJButton label="Annuler les modifications" scale="sm" variant="secondary" disabled={!dirty}
              onClick={() => { setCreating(false); setDraftIds(null); setArea(false); }} />
            {!creating && selected && <NJButton label="Supprimer la sélection" scale="sm" variant="destructive"
              disabled={!!users.length || dirty} onClick={() => { p.onDelete(selected.id); setSelectedId(''); }} />}
          </div>
        </>}
        {users.length > 0 && <NJText scale="xs">Une sélection utilisée ne peut pas être supprimée. Les copies dans les plans sont préservées.</NJText>}
      </>}
      {message && <NJText role="status" scale="xs">{message}</NJText>}
    </section>
    {p.geo ? <MapPane geo={p.geo} overlay={overlay} /> : <NJText role="status">Chargement de la carte…</NJText>}
  </div>;
}
